import { useEffect, useState } from "react";
import { X, Download, FileDown, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { api, fmtCHF, fmtDate, downloadFile, formatApiError } from "@/lib/api";
import { FACT_TYPE_LABELS, PAYMENT_STATUS_LABELS } from "@/lib/organs";
import { SectionTitle } from "@/components/Shared";
import PipelineStepper from "@/components/PipelineStepper";
import SampleContent from "@/components/SampleContent";
import OriginalPreview from "@/components/OriginalPreview";
import { useCompany } from "@/context/CompanyContext";

export default function DocDetail({ detail, onClose, onChanged }) {
  const { companyId } = useCompany() || {};
  // Never retain a source, text or fullscreen panel from the previous company.
  if (!detail || !companyId || detail.company_id !== companyId) return null;
  return <DocumentDialog key={`${companyId}:${detail.doc_id}`} detail={detail} companyId={companyId} onClose={onClose} onChanged={onChanged} />;
}

function DocumentDialog({ detail, companyId, onClose, onChanged }) {
  const [declaring, setDeclaring] = useState(false);
  const [declMethod, setDeclMethod] = useState("");
  const [declDate, setDeclDate] = useState("");
  const [tab, setTab] = useState("original");
  const [page, setPage] = useState(1);
  const scoped = { headers: { "x-company-id": companyId } };
  useEffect(() => {
    const escape = (event) => {
      if (event.key === "Escape" && !event.defaultPrevented && !document.fullscreenElement && !document.querySelector('[data-testid="original-preview"][data-fullscreen="true"]')) onClose();
    };
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [onClose]);

  const pipeline = (detail.sample_only || detail.history_processing) ? [
    ...(detail.pipeline || []).filter((s) => ["acquisizione", "classificazione", "collegamento"].includes(s.stage)),
    { stage: "estrazione", status: detail.processing_observations?.extraction?.status || "unknown", result: detail.processing_observations?.extraction?.method || "Estrazione non attestata" },
    { stage: "lettura_ai", status: detail.processing_observations?.ai_analysis?.status || "not_run", reason: "Testo estratto/OCR e classificazione mediante regole sono distinti dall’analisi AI." },
    { stage: "verifica", status: detail.review_state === "NEEDS_REVIEW" ? "requires_confirmation" : "unknown" },
    { stage: "contabilizzazione", status: detail.accounting_finalized ? "completed" : "not_run", result: detail.accounting_finalized ? "Stato registrato" : "Nessuna registrazione contabile definitiva" },
  ] : detail.pipeline || [];
  const pipelineLabels = (detail.sample_only || detail.history_processing) ? { acquisizione: "Acquisizione", estrazione: "Estrazione / OCR", lettura_ai: "Analisi AI", classificazione: "Classificazione", verifica: "Revisione", contabilizzazione: "Contabilizzazione", collegamento: "Collegamenti" } : undefined;

  const canDeclarePaid = !(detail.sample_only || detail.history_processing) && detail.doc_type === "Fattura fornitore" && (detail.payment_status || "open") === "open";

  const declarePaid = async () => {
    try {
      await api.post(`/documents/${detail.doc_id}/declare-paid`, {
        payment_method: declMethod || null, payment_date: declDate || null,
      }, scoped);
      toast.success("Pagamento dichiarato registrato (fonte: conferma umana). Riconciliazione in attesa.");
      setDeclaring(false);
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const downloadOriginal = async () => {
    try {
      await downloadFile(`/documents/${detail.doc_id}/file`, detail.filename, scoped);
      toast.success("Originale scaricato · integrità verificata via checksum");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Originale non disponibile");
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-center bg-black/60 backdrop-blur-sm md:p-4" data-testid="doc-detail-overlay" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-labelledby="document-detail-heading" className="w-full max-w-7xl h-full glass-strong flex flex-col overflow-hidden md:rounded-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 p-4 border-b border-white/10 shrink-0">
          <div className="min-w-0">
            <div id="document-detail-heading" className="text-base font-semibold break-words" data-testid="doc-detail-title">{detail.supplier || detail.filename}</div>
            <div className="text-xs text-gray-500">{detail.doc_type} · {fmtDate(detail.date)} · <span className="font-mono">{fmtCHF(detail.amount)}</span></div>
            <div data-testid="doc-immediate-source-state" className="text-xs text-gray-300 mt-2 flex flex-wrap gap-3">
              <span>Fonte: {detail.source_provenance?.source || detail.channel || "Non attestata"}</span>
              <span>Pagina {page}</span>
              <strong className="text-amber-300">{detail.review_state || detail.status}</strong>
            </div>
            {(detail.review_warnings || []).map((warning) => <p key={warning.code} data-testid="doc-date-discordance" role="status" className="text-sm text-amber-200 border border-amber-400/30 rounded-xl p-2 mt-2">{warning.message}</p>)}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 shrink-0" data-testid="doc-detail-close"><X size={18} /></button>
        </div>

        <div role="tablist" aria-label="Vista documento" className="md:hidden flex gap-2 px-4 pt-3 shrink-0">
          {[['original', 'Originale'], ['text', 'Testo']].map(([value, label]) => <button key={value} type="button" role="tab" aria-selected={tab === value} aria-controls={`document-${value}`} onClick={() => setTab(value)} className={`chip ${tab === value ? "!text-cyan-200 !border-cyan-400" : ""}`}>{label}</button>)}
        </div>
        <div data-testid="doc-detail-columns" className="grid grid-cols-1 md:grid-cols-2 gap-3 flex-1 min-h-0 p-3">
          <div id="document-original" className={`${tab === 'original' ? 'flex' : 'hidden'} md:flex flex-col min-h-0 overflow-hidden rounded-2xl border border-white/10`}>
            <h2 className="text-sm font-semibold px-3 py-2">Originale · pagina {page}</h2>
            <div className="flex-1 min-h-0"><OriginalPreview detail={detail} companyId={companyId} page={page} onPageChange={setPage} /></div>
          </div>
          <div id="document-text" className={`${tab === 'text' ? 'block' : 'hidden'} md:block overflow-y-auto min-h-0 rounded-2xl border border-white/10 p-4 space-y-4`}>
        <h2 className="text-sm font-semibold">Testo e revisione · pagina {page}</h2>

        {detail.note && <div className="text-xs text-amber-300/90 bg-amber-400/5 border border-amber-400/15 rounded-xl px-3 py-2">{detail.note}</div>}
        <SampleContent detail={detail} selectedPage={page} />
        {!(detail.sample_only || detail.history_processing) && detail.extracted_text && <pre className="whitespace-pre-wrap break-words font-sans text-sm text-gray-300" data-testid="legacy-extracted-text">{detail.extracted_text}</pre>}

        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
          {detail.actual_payment_method && <div><span className="text-gray-600">Metodo: </span><span className="text-gray-300">{detail.actual_payment_method}</span></div>}
          {detail.payment_status && (
            <div data-testid="doc-payment-status"><span className="text-gray-600">Pagamento: </span>
              <span style={{ color: (PAYMENT_STATUS_LABELS[detail.payment_status] || {}).color }}>{(PAYMENT_STATUS_LABELS[detail.payment_status] || {}).label || detail.payment_status}</span>
            </div>
          )}
          {detail.expected_payment_method && <div><span className="text-gray-600">Previsto: </span><span className="text-gray-300">{detail.expected_payment_method}</span></div>}
          {detail.payment_terms_days != null && <div><span className="text-gray-600">Termini: </span><span className="text-gray-300">{detail.payment_terms_days} giorni</span></div>}
          {detail.invoice_number && <div><span className="text-gray-600">N. fattura: </span><span className="text-gray-300">{detail.invoice_number}</span></div>}
          {detail.due_date && <div><span className="text-gray-600">Scadenza: </span><span className="text-gray-300">{fmtDate(detail.due_date)}</span></div>}
          {detail.person && <div><span className="text-gray-600">Persona: </span><span className="text-gray-300">{detail.person}</span></div>}
          {detail.management_category && <div><span className="text-gray-600">Gestionale: </span><span className="text-gray-300">{detail.management_category}</span></div>}
          {detail.accounting_category && <div><span className="text-gray-600">Contabile: </span><span className="text-gray-300">{detail.accounting_category}</span></div>}
        </div>

        {canDeclarePaid && (
          <div className="rounded-2xl border border-amber-400/20 bg-amber-400/5 p-4" data-testid="declare-paid-box">
            {!declaring ? (
              <button data-testid="declare-paid-open" onClick={() => setDeclaring(true)} className="btn-ghost !text-xs w-full">
                <BadgeCheck size={13} /> Dichiara questa fattura come pagata
              </button>
            ) : (
              <div className="space-y-3">
                <p className="text-[11px] text-amber-200/80 leading-relaxed">
                  La tua dichiarazione viene registrata come fonte umana separata — non crea alcuna evidenza bancaria.
                  La riconciliazione resterà in attesa fino alla prova bancaria/documentale.
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {[["banca", "Bonifico bancario"], ["contanti", "Contanti"], ["carta_aziendale", "Carta aziendale"], ["altro", "Altro"]].map(([v, l]) => (
                    <button key={v} data-testid={`declare-method-${v}`} onClick={() => setDeclMethod(v)}
                      className={`chip !cursor-pointer ${declMethod === v ? "!text-cyan-200 !border-cyan-400/60 !bg-cyan-400/15" : ""}`}>{l}</button>
                  ))}
                </div>
                <input type="date" data-testid="declare-date" value={declDate} onChange={(e) => setDeclDate(e.target.value)} className="input-dark !py-2 !text-xs" />
                <div className="flex gap-2">
                  <button data-testid="declare-paid-submit" onClick={declarePaid} className="btn-primary !py-2 !text-xs flex-1">Registra dichiarazione</button>
                  <button onClick={() => setDeclaring(false)} className="btn-ghost !py-2 !text-xs">Annulla</button>
                </div>
              </div>
            )}
          </div>
        )}

        <div>
          <SectionTitle>Pipeline canonica</SectionTitle>
          <PipelineStepper pipeline={pipeline} labels={pipelineLabels} detailed />
        </div>

        <details data-testid="document-technical-details">
          <summary className="text-xs text-gray-400 cursor-pointer">Dettagli tecnici · integrità e storage</summary>
          {detail.storage_ref?.checksum ? (
            <div className="text-[11px] font-mono text-gray-400 space-y-1" data-testid="doc-detail-storage">
              <div>provider: {detail.storage_ref.storage_provider}</div>
              <div className="break-all">object_key: {detail.storage_ref.object_key}</div>
              <div className="break-all">sha256: {detail.storage_ref.checksum}</div>
              <div>versione: {detail.storage_ref.version} · {detail.storage_ref.sync_status}</div>
            </div>
          ) : (
            <p className="text-[11px] text-gray-500" data-testid="doc-detail-legacy">
              Originale non conservato (documento pre-Phase 2). Presenti solo metadati.
            </p>
          )}
        </details>

        <div>
          <SectionTitle>Evidenze collegate</SectionTitle>
          {(detail.evidence || []).length === 0 && <p className="text-[11px] text-gray-500">Nessuna evidenza.</p>}
          {(detail.evidence || []).map((ev) => (
            <div key={ev.evidence_id} className="text-[11px] text-gray-400 rounded-xl border border-white/5 px-3 py-2 mb-1.5">
              {FACT_TYPE_LABELS[ev.business_fact_type] || ev.business_fact_type} · {ev.verification_status}{ev.verification_mode ? ` (${ev.verification_mode})` : ""} · {ev.accounting_status}
            </div>
          ))}
        </div>

        {((detail.audit || []).length > 0 || (detail.confirmations || []).length > 0) && (
          <div data-testid="doc-detail-audit">
            <SectionTitle>Audit trail</SectionTitle>
            {(detail.confirmations || []).map((c) => (
              <div key={c.confirmation_id} className="text-[11px] text-cyan-200/80 rounded-xl border border-cyan-400/15 bg-cyan-400/5 px-3 py-2 mb-1.5"
                data-testid={`confirmation-${c.confirmation_id}`}>
                Conferma umana · {c.field}: {c.previous == null ? "—" : String(c.previous)} → {c.deferred ? "rimandato" : String(c.new)} · {c.source}
              </div>
            ))}
            {(detail.audit || []).map((a, i) => (
              <div key={i} className="text-[11px] text-gray-500 px-3 py-1.5">
                {a.event}{a.details ? ` · ${a.details}` : ""} · {new Date(a.at).toLocaleString("it-IT")}
              </div>
            ))}
          </div>
        )}

          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 p-3 border-t border-white/10 shrink-0">
          <button data-testid="doc-detail-download" onClick={downloadOriginal} className="btn-ghost !text-xs" disabled={!detail.storage_ref?.checksum}>
            <Download size={13} /> Scarica originale
          </button>
          <button data-testid="doc-detail-export" onClick={() => downloadFile(`/documents/${detail.doc_id}/export`, `dad-doc-${detail.doc_id}.json`, scoped).then(() => toast.success("Export documento scaricato"))} className="btn-ghost !text-xs">
            <FileDown size={13} /> Esporta JSON
          </button>
        </div>
      </div>
    </div>
  );
}
